'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Loader2,
  ChevronRight,
  ChevronLeft,
  Check,
  ClipboardList,
  FileText,
  Globe,
  PencilRuler,
  Brain,
  Calculator,
} from 'lucide-react';
import { ProBadge } from '@/features/subscription/ui/ProBadge';
import { Textarea } from '@/shared/ui/shadcn/textarea';
import { Switch } from '@/shared/ui/shadcn/switch';
import { NoticeBanner } from '@/shared/ui/NoticeBanner';
import { cn } from '@/shared/lib/cn';
import { SettingsCard } from '@/shared/ui/settings/SettingsCard';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import {
  useConceptAlbumSettings,
  useAlbum,
  useAlbumCostPrediction,
  useAlbumEstimatePicker,
  isPremiumAlbumTemplate,
  MATERIALS_SECTION_TOGGLE_ROW,
  SECONDARY_SECTION_TOGGLE_ROWS,
  AlbumEstimateOptions,
  AlbumRenderThumbnailStrip,
  type AlbumCostPredictionParams,
  type AlbumLanguage,
  type AlbumMeasurementSystem,
  type AlbumSectionToggleRow,
  type AlbumTemplate,
} from '@/features/design/album';
import { sortAlbumLocalesForUser, getLocaleByCode } from '@/shared/constants';
import { useJobQueueStore } from '@/features/design/job-queue';
import { useRendersForConcept } from '@/features/design/render/model/renders-cache';
import { usePlan } from '@/features/subscription/model/subscription.selectors';
import { useSubscriptionLimits } from '@/features/subscription';
import { RenderStatus } from '@/shared/constants';
import { isUserVisibleRenderType } from '@/types/render';
import { useConceptStore } from '@/features/design/concept/model/concept-editor-store';
import { usePassPaywallStore } from '@/shared/stores/pass-paywall-store';
import type { ConceptAngle } from '@/types/concept';

interface AlbumTabContentProps {
  conceptId: string;
  /** Project the concept belongs to — required to list the concept's estimates
   *  for the album Estimate picker (FE4). */
  projectId: string;
  conceptAngles: ConceptAngle[];
  isArchived?: boolean;
}

type AlbumPanelView =
  | 'main'
  | 'template'
  | 'language'
  | 'requirements'
  | 'estimate'
  | 'units';

// Render order for the template (design) axis. `standard` first (free), then
// the premium (Designer-tier) templates. Mirrors the Apple/Android catalog order.
const ALBUM_TEMPLATE_OPTIONS: readonly AlbumTemplate[] = [
  'standard',
  'architect',
  'studio',
];

// Units picker order — Auto (server decides, default) first, then the two
// explicit systems. Mirrors Apple/Android.
const ALBUM_MEASUREMENT_SYSTEM_OPTIONS: readonly AlbumMeasurementSystem[] = [
  'auto',
  'metric',
  'imperial',
];

export function AlbumTabContent({
  conceptId,
  projectId,
  conceptAngles,
  isArchived = false,
}: AlbumTabContentProps) {
  const tAlbum = useTranslations('album');
  const tCommon = useTranslations('common');
  const tEstimate = useTranslations('common.estimate');
  // Extended-thinking row reuses the render namespace title (same wording as
  // the render-settings toggle) — no album-specific copy needed.
  const tExtended = useTranslations('render.settings.extendedThinking');
  const plan = usePlan();
  const planData = plan && 'attributes' in plan ? plan.attributes : plan;
  const hasAdvancedAlbumOptions =
    planData?.is_album_advanced_options_available ?? false;
  // Designer-tier access to premium album DESIGN templates (architect/studio).
  // With access the premium rows are freely selectable (no badge); without it
  // each shows a DESIGNER lock badge and a tap routes to the Designer paywall.
  // Mirrors the closed named-model row.
  const isAlbumTemplatesAvailable =
    planData?.is_album_templates_available ?? false;
  // Locked PRO album rows open the server-driven Pass paywall instead of
  // dead-clicking — same pattern as the extended-thinking row.
  const openPassPaywall = usePassPaywallStore((s) => s.open);
  // Extended thinking is a Pro+ perk (EntitlementFeatureMap: extended_thinking).
  // Same gate as the render-settings ExtendedThinkingSection: when the plan
  // lacks it the switch is read-only and a tap routes to the Pro paywall.
  const limits = useSubscriptionLimits();
  const hasExtendedThinking = limits?.has_extended_thinking ?? false;

  // Album settings from store
  const albumSettings = useConceptAlbumSettings(conceptId);
  const {
    selectedTemplate,
    includeBlueprints,
    includeMaterials,
    includePreparationGuide,
    includeSafetyRequirements,
    includeBuildingRegulations,
    measurementSystem,
    selectedLanguage: storedSelectedLanguage,
    requirements: storedRequirements,
    isExtendedThinking,
    updateSetting,
  } = albumSettings;

  // Apple parity (`AlbumPanelContent`): the panel swaps its content area
  // between `main` / `language` / `requirements` rather than overlaying a
  // nested sheet on top of the settings. The Generate button stays anchored
  // to the bottom across all three views.
  const [view, setView] = useState<AlbumPanelView>('main');
  // Sorted once when the language view opens — stable during selection.
  const [languageOrder, setLanguageOrder] = useState(() =>
    sortAlbumLocalesForUser(storedSelectedLanguage)
  );

  const selectedLanguageName =
    getLocaleByCode(storedSelectedLanguage)?.name ?? storedSelectedLanguage;

  const openLanguageView = () => {
    setLanguageOrder(sortAlbumLocalesForUser(storedSelectedLanguage));
    setView('language');
  };

  // Cost-estimate picker (FE4) — this design's estimates first (default),
  // "New estimate", then sibling designs' estimates greyed at the end. All
  // defaulting/stale-guard logic lives in the shared hook.
  const estimatePicker = useAlbumEstimatePicker(projectId, conceptId);
  const {
    currentDesignEstimates,
    otherDesignEstimates,
    selectedEstimateId,
    setSelectedEstimateId,
    isEstimateIncluded,
    excludeEstimate,
    isPlanLocked: isEstimateLocked,
  } = estimatePicker;
  const selectedEstimate = [
    ...currentDesignEstimates,
    ...otherDesignEstimates,
  ].find((e) => e.id === selectedEstimateId);
  // Row value for the Estimate setting: the localized document name when one
  // is picked (estimates carry no user-set title — Apple/Android row-value
  // parity), "New estimate", or "Don't include".
  const estimateRowValue =
    isEstimateLocked || !isEstimateIncluded
      ? tEstimate('album.dontInclude')
      : selectedEstimate
        ? tEstimate('card.untitled')
        : tEstimate('album.generateNew');

  // Album generation hook
  const { generateAlbum, isGenerating: isGeneratingAlbum } = useAlbum({
    conceptId,
  });

  // Check for album in queue
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const albumInQueue = jobQueue.find(
    (item) => item.conceptId === conceptId && item.jobType === 'album'
  );
  const hasAlbumInQueue =
    albumInQueue &&
    (albumInQueue.status === RenderStatus.PENDING ||
      albumInQueue.status === RenderStatus.PROCESSING);
  const isAlbumGenerating = isGeneratingAlbum || hasAlbumInQueue;

  // Renders owned by TanStack Query (`cacheKeys.renders.list(conceptId)`).
  const rendersForConcept = useRendersForConcept(conceptId);
  const selectedRenderIdsFromStore = useConceptStore(
    (state) => state.ui.selectedRenderId
  );

  // Calculate selected renders for album generation
  const selectedRendersForAlbum = useMemo(() => {
    const result: Record<string, string> = {};
    conceptAngles.forEach((ca) => {
      const completedRenders = rendersForConcept
        .filter(
          (r) =>
            r.relationships?.concept_angle?.data?.id === ca.id &&
            r.attributes.status === RenderStatus.COMPLETED &&
            isUserVisibleRenderType(r.attributes.render_type)
        )
        .sort(
          (a, b) =>
            new Date(a.attributes.created_at).getTime() -
            new Date(b.attributes.created_at).getTime()
        );
      if (completedRenders.length > 0) {
        const selectedRenderId = selectedRenderIdsFromStore[ca.id];
        const selectedRender = selectedRenderId
          ? completedRenders.find((r) => r.id === selectedRenderId) ||
            completedRenders[completedRenders.length - 1]
          : completedRenders[completedRenders.length - 1];
        if (selectedRender) {
          result[ca.id] = selectedRender.id;
        }
      }
    });
    return result;
  }, [conceptAngles, rendersForConcept, selectedRenderIdsFromStore]);

  // Thumbnail URLs for the exact renders the album will use — resolved from the
  // selected-render map so the strip matches `selected_renders` 1:1.
  const selectedRenderUrls = useMemo(() => {
    return Object.values(selectedRendersForAlbum)
      .map((renderId) => {
        const render = rendersForConcept.find((r) => r.id === renderId);
        return (
          render?.relationships?.file?.meta?.url ?? render?.attributes.file_url
        );
      })
      .filter((url): url is string => Boolean(url));
  }, [selectedRendersForAlbum, rendersForConcept]);

  const renderedCount = Object.keys(selectedRendersForAlbum).length;
  const totalCount = conceptAngles.length;
  const hasPartialRenders = renderedCount > 0 && renderedCount < totalCount;
  const useSecondaryVariant = totalCount > 1 && hasPartialRenders;

  // Server-predicted token cost for the CURRENT settings — shown as a suffix
  // on the Generate button. The cost-relevant params mirror the generate
  // request below 1:1 (memoized so the hook's debounce is stable).
  const costPredictionParams = useMemo<AlbumCostPredictionParams>(
    () => ({
      renders_count: renderedCount,
      include_materials: includeMaterials,
      include_blueprints: includeBlueprints,
      include_preparation_guide: includePreparationGuide,
      include_safety_requirements: includeSafetyRequirements,
      include_building_regulations: includeBuildingRegulations,
      // Same client-side gate as the generate request: never predict with
      // extended thinking the plan can't use.
      is_extended_thinking: hasExtendedThinking && isExtendedThinking,
      // True only for the "New estimate" picker option — exactly when the
      // generate request spreads `generate_new_estimate: true` (see
      // useAlbumEstimatePicker.estimatePayload).
      generate_new_estimate:
        'generate_new_estimate' in estimatePicker.estimatePayload,
    }),
    [
      renderedCount,
      includeMaterials,
      includeBlueprints,
      includePreparationGuide,
      includeSafetyRequirements,
      includeBuildingRegulations,
      hasExtendedThinking,
      isExtendedThinking,
      estimatePicker.estimatePayload,
    ]
  );
  const {
    tokens: predictedTokens,
    isSignificantCost,
    warningMessage: costWarningMessage,
  } = useAlbumCostPrediction(conceptId, costPredictionParams);

  // Apple parity (AlbumGeneratorView): no client-side plan/balance preflight.
  // The server enforces limits and returns a 422 with an inline paywall
  // payload; `use-album.ts` routes it to the shared LimitPaywallDialog.
  const handleGenerateAlbum = () => {
    if (isArchived || isAlbumGenerating) return;
    // No empty-renders guard: the request fires and the server's localized
    // `main_render_required` message surfaces as an error toast (use-album
    // onError; API-driven messages — same contract as the estimate CTA).
    const selectedRenderIds = Object.values(selectedRendersForAlbum);

    generateAlbum({
      // Template is the design axis. Send the actually selected template —
      // Designer holders can pick architect/studio; the server falls back to
      // standard for any not-yet-built design. Blueprints are an independent
      // free toggle that replaces the old full/short split.
      template: selectedTemplate,
      // Content-section flags — always sent (all default ON) so the server
      // records the snapshot explicitly. `include_blueprints` gates ONLY the
      // facade projections (blueprint drawings).
      include_blueprints: includeBlueprints,
      include_materials: includeMaterials,
      include_preparation_guide: includePreparationGuide,
      include_safety_requirements: includeSafetyRequirements,
      include_building_regulations: includeBuildingRegulations,
      language: storedSelectedLanguage,
      // Measurement system: sent only when the user explicitly picked Metric
      // or Imperial — "Auto" omits the field so the server decides.
      ...(measurementSystem !== 'auto'
        ? { measurement_system: measurementSystem }
        : {}),
      selected_renders: selectedRenderIds,
      // Estimate choice: explicit estimate_id (may be a sibling design's) or
      // generate_new_estimate — see useAlbumEstimatePicker.estimatePayload.
      ...estimatePicker.estimatePayload,
      ...(storedRequirements ? { requirements: storedRequirements } : {}),
      // Gate client-side too: never request extended thinking the plan can't use.
      is_extended_thinking: hasExtendedThinking && isExtendedThinking,
    });
  };

  // Content-section toggle row — free Switch row with a trailing divider.
  // Rendered in two slices (Materials first, the rest after the Estimate
  // row), so the markup lives in one place.
  const renderSectionToggleRow = ({
    key,
    icon: Icon,
    labelKey,
  }: AlbumSectionToggleRow) => (
    <div key={key}>
      <div className="px-5 py-3">
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
          <span className="text-list-item-subtitle flex-1">
            {tAlbum(`settings.${labelKey}.title`)}
          </span>
          <Switch
            checked={albumSettings[key]}
            onCheckedChange={(checked) => updateSetting(key, checked)}
            disabled={isAlbumGenerating}
            aria-label={tAlbum(`settings.${labelKey}.title`)}
          />
        </div>
      </div>
      <div className="border-t border-border/30 mx-5" />
    </div>
  );

  // ---------- Detail header (shared by language/requirements views) ----------
  const detailHeader = (title: string) => (
    <div className="px-2 py-2 flex-shrink-0 flex items-center gap-2">
      <div className="flex items-center justify-start min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setView('main')}
          aria-label={tCommon('back')}
          className="inline-flex items-center justify-center size-9 rounded-md text-primary hover:text-primary/80 transition-colors"
        >
          <ChevronLeft className="size-5 rtl:-scale-x-100" />
        </button>
      </div>
      <span className="text-subpanel-title text-foreground truncate">
        {title}
      </span>
      <div className="flex-1" />
    </div>
  );

  // ---------- Footer (sticky Generate button — always visible) ----------
  const footer = (
    <div className="px-4 py-3 flex flex-shrink-0 justify-center border-t border-border/40 bg-muted">
      <Button
        variant={useSecondaryVariant ? 'secondary' : 'default'}
        onClick={handleGenerateAlbum}
        size="cta"
        // Stays ACTIVE with no completed renders — see handleGenerateAlbum.
        disabled={isArchived || isAlbumGenerating}
      >
        {isAlbumGenerating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {tAlbum('generatingAlbum')}
          </>
        ) : (
          <>
            {tAlbum('generateAlbum')}
            {/* Server-predicted token cost — suffix only when the server
                flags the cost as significant (threshold lives server-side;
                loading/error/below-threshold → plain label, no price). */}
            {predictedTokens !== undefined && isSignificantCost && (
              <span className="inline-flex items-center gap-1">
                · ≈{predictedTokens}
                <WalnutIcon className="h-3.5 w-[11px] shrink-0" />
              </span>
            )}
          </>
        )}
      </Button>
    </div>
  );

  // ---------- Main settings view ----------
  if (view === 'main') {
    return (
      <>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Renders that will be packed into the album — shown first so the
              selection is visible before any setting. */}
          {selectedRenderUrls.length > 0 && (
            <div className="px-4 pt-3">
              <AlbumRenderThumbnailStrip urls={selectedRenderUrls} />
            </div>
          )}

          {/* Partial renders warning */}
          {hasPartialRenders && (
            <div className="px-4 pt-2">
              <NoticeBanner
                variant="info"
                message={tAlbum('partialRendersWarning', {
                  renderedCount,
                  totalCount,
                })}
              />
            </div>
          )}

          {/* Album settings - grouped cards */}
          <div className="px-4 space-y-3 py-3">
            <SettingsCard title={tAlbum('sections.settings')}>
              {/* Template Row — the DESIGN axis. Opens the template drill-down
                  view; the option list (free `standard`, Designer-tier
                  `architect`/`studio`) lives there. Mirrors Apple/Android and
                  the single-column AlbumPanelSettings. */}
              <button
                onClick={() => setView('template')}
                disabled={isAlbumGenerating}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
              >
                <FileText className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-list-item-subtitle flex-1 text-start">
                  {tAlbum('settings.type.label')}
                </span>
                <span className="text-list-item-subtitle text-muted-foreground">
                  {tAlbum(`settings.type.${selectedTemplate}`)}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
              </button>

              <div className="border-t border-border/30 mx-5" />

              {/* Language Row */}
              <button
                onClick={openLanguageView}
                disabled={isAlbumGenerating}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
              >
                <Globe className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-list-item-subtitle flex-1 text-start">
                  {tAlbum('settings.language')}
                </span>
                <span className="text-list-item-subtitle text-muted-foreground">
                  {selectedLanguageName}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
              </button>

              <div className="border-t border-border/30 mx-5" />

              {/* Content-section toggles — materials / blueprints /
                  preparation guide / safety requirements / building
                  regulations. Free Switch rows, all default ON;
                  `include_blueprints` gates ONLY the facade projections.
                  The Estimate row sits right after Materials — order
                  mirrors Apple/Android. */}
              {renderSectionToggleRow(MATERIALS_SECTION_TOGGLE_ROW)}

              {/* Estimate Row (FE4) — defaults to this design's latest ready
                  estimate; the drill-down also offers "Don't include", "New
                  estimate" and sibling designs' estimates. Estimate-in-album
                  is a Pro perk (cost_estimate): when locked the PRO badge is
                  the only marker, the value reads "Don't include" and a tap
                  routes to the Pro Pass paywall — mirrors Requirements. */}
              <button
                onClick={
                  isEstimateLocked
                    ? () => openPassPaywall('pro')
                    : () => setView('estimate')
                }
                disabled={isAlbumGenerating}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
              >
                <Calculator className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-list-item-subtitle text-start">
                  {tEstimate('album.label')}
                </span>
                <ProBadge tier="pro" isLocked={isEstimateLocked} />
                <span className="flex-1" />
                <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[140px]">
                  {estimateRowValue}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
              </button>

              <div className="border-t border-border/30 mx-5" />

              {SECONDARY_SECTION_TOGGLE_ROWS.map(renderSectionToggleRow)}

              {/* Requirements — PRO-gated; shapes WHAT the album contains
                  (alongside Template/Language/Blueprints). A locked tap opens
                  the Pro paywall; with access it opens the requirements
                  editor. Mirrors Apple/Android. */}
              <button
                onClick={
                  hasAdvancedAlbumOptions
                    ? () => setView('requirements')
                    : () => openPassPaywall('pro')
                }
                className="w-full flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50"
              >
                <ClipboardList className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                {/* Apple parity: PRO badge sits to the LEFT, right after the
                    row title — not on the trailing edge. */}
                <span className="text-list-item-subtitle text-start">
                  {tAlbum('settings.requirements.label')}
                </span>
                <ProBadge tier="pro" isLocked={!hasAdvancedAlbumOptions} />
                <span className="flex-1" />
                {hasAdvancedAlbumOptions && storedRequirements && (
                  <span className="text-list-item-subtitle text-muted-foreground truncate max-w-[120px]">
                    {storedRequirements.substring(0, 40)}
                    {storedRequirements.length > 40 ? '...' : ''}
                  </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
              </button>

              <div className="border-t border-border/30 mx-5" />

              {/* Units Row — measurement system for the album content (Auto /
                  Metric / Imperial). Free for everyone; Auto (default) lets
                  the server pick and omits the field from the generate
                  request. Last Settings-group row — mirrors Apple/Android. */}
              <button
                onClick={() => setView('units')}
                disabled={isAlbumGenerating}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-muted/50 transition-colors"
              >
                <PencilRuler className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-list-item-subtitle flex-1 text-start">
                  {tAlbum('settings.units.label')}
                </span>
                <span className="text-list-item-subtitle text-muted-foreground">
                  {tAlbum(`settings.units.${measurementSystem}`)}
                </span>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 rtl:-scale-x-100" />
              </button>
            </SettingsCard>

            {/* Controls group — generation controls (HOW the album is built):
                Extended thinking. Mirrors Apple/Android. The doubled top gap
                (container space-y-3 + this card's pt-3) sets it apart from the
                Settings group above. */}
            <SettingsCard title={tAlbum('sections.controls')} className="pt-3">
              {/* Extended thinking — PRO-gated. Locked tap opens the Pro paywall;
                  with access the whole row toggles. `div role="button"` (not a
                  <button>) so the nested Switch stays valid HTML. Mirrors the
                  render-settings ExtendedThinkingSection. */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!hasExtendedThinking) {
                    openPassPaywall('pro');
                    return;
                  }
                  updateSetting('isExtendedThinking', !isExtendedThinking);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!hasExtendedThinking) {
                      openPassPaywall('pro');
                      return;
                    }
                    updateSetting('isExtendedThinking', !isExtendedThinking);
                  }
                }}
                className="w-full flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/50 cursor-pointer"
              >
                <Brain className="h-5 w-5 text-primary shrink-0" strokeWidth={1.5} />
                <span className="text-list-item-subtitle flex-1 text-start">
                  {tExtended('title')}
                </span>
                <ProBadge tier="pro" isLocked={!hasExtendedThinking} />
                <div
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Switch
                    checked={hasExtendedThinking && isExtendedThinking}
                    onCheckedChange={(checked) => {
                      if (!hasExtendedThinking) {
                        openPassPaywall('pro');
                        return;
                      }
                      updateSetting('isExtendedThinking', checked);
                    }}
                    disabled={!hasExtendedThinking || isAlbumGenerating}
                    aria-label={tExtended('title')}
                  />
                </div>
              </div>
            </SettingsCard>

            {/* Significant-cost warning — server-computed flag + server-
                localized text (parity with Apple): shown right before the
                Generate button in the sticky footer below, only when the
                current settings make generation token-expensive. */}
            {costWarningMessage != null && !isAlbumGenerating && (
              <NoticeBanner variant="warning" message={costWarningMessage} />
            )}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Template detail view ----------
  if (view === 'template') {
    return (
      <>
        {detailHeader(tAlbum('settings.type.label'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          {/* Template = design axis. `standard` is free; `architect`/`studio`
              are premium (Designer-tier). Without Designer access a premium
              row shows a DESIGNER lock badge and a tap routes to the Designer
              Pass paywall; with access the row is freely selectable (no
              badge). Mirrors the closed named-model row. */}
          <div className="space-y-2">
            {ALBUM_TEMPLATE_OPTIONS.map((option) => {
              const isPremium = isPremiumAlbumTemplate(option);
              const locked = isPremium && !isAlbumTemplatesAvailable;
              const isSelected = !locked && selectedTemplate === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    // Locked premium template (no Designer access) → upsell
                    // the Designer Pass paywall, never select. Otherwise the
                    // row is selectable (incl. architect/studio for Designer
                    // holders). Mirrors the closed named-model row.
                    if (locked) {
                      openPassPaywall('designer');
                      return;
                    }
                    updateSetting('selectedTemplate', option);
                    setView('main');
                  }}
                  disabled={isAlbumGenerating}
                  className={cn(
                    'w-full flex flex-col gap-1 p-4 rounded-lg border-2 transition-colors text-start',
                    isSelected ? 'border-primary bg-primary/5' : 'border-border',
                    locked ? 'opacity-50' : 'hover:bg-muted/50'
                  )}
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <span className="text-list-item-subtitle">
                      {tAlbum(`settings.type.${option}`)}
                    </span>
                    {/* DESIGNER lock badge on premium templates only while
                        locked — ProBadge renders nothing once unlocked. */}
                    {isPremium && <ProBadge tier="designer" isLocked={locked} />}
                    {isSelected && (
                      <Check className="h-4 w-4 text-primary flex-shrink-0" />
                    )}
                  </div>
                  {option === 'standard' && (
                    <span className="text-card-meta text-muted-foreground">
                      {tAlbum('settings.type.standardDescription')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Language detail view ----------
  if (view === 'language') {
    return (
      <>
        {detailHeader(tAlbum('settings.language'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          <div className="space-y-2">
            {languageOrder.map((lang) => {
              const isSelected = lang.code === storedSelectedLanguage;
              return (
                <button
                  key={lang.code}
                  onClick={() => {
                    updateSetting(
                      'selectedLanguage',
                      lang.code as AlbumLanguage
                    );
                    setView('main');
                  }}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-start',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  )}
                >
                  <span className="text-list-item-subtitle">
                    {lang.name} ({lang.nativeName})
                  </span>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Estimate picker detail view (FE4) ----------
  if (view === 'estimate') {
    return (
      <>
        {detailHeader(tEstimate('album.label'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          <AlbumEstimateOptions
            currentDesignEstimates={currentDesignEstimates}
            otherDesignEstimates={otherDesignEstimates}
            selectedEstimateId={selectedEstimateId}
            isEstimateIncluded={isEstimateIncluded}
            onSelect={(id) => {
              setSelectedEstimateId(id);
              setView('main');
            }}
            onExclude={() => {
              excludeEstimate();
              setView('main');
            }}
            disabled={isAlbumGenerating}
          />
        </div>
        {footer}
      </>
    );
  }

  // ---------- Units detail view ----------
  if (view === 'units') {
    return (
      <>
        {detailHeader(tAlbum('settings.units.label'))}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
          <div className="space-y-2">
            {ALBUM_MEASUREMENT_SYSTEM_OPTIONS.map((option) => {
              const isSelected = measurementSystem === option;
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    updateSetting('measurementSystem', option);
                    setView('main');
                  }}
                  disabled={isAlbumGenerating}
                  className={cn(
                    'w-full flex items-center justify-between p-4 rounded-lg border-2 transition-colors text-start',
                    isSelected
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  )}
                >
                  <span className="text-list-item-subtitle">
                    {tAlbum(`settings.units.${option}`)}
                  </span>
                  {isSelected && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
        {footer}
      </>
    );
  }

  // ---------- Requirements detail view ----------
  return (
    <>
      {detailHeader(tAlbum('settings.requirements.label'))}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-3">
        <Textarea
          value={storedRequirements}
          onChange={(e) => updateSetting('requirements', e.target.value)}
          placeholder={tAlbum('settings.requirements.placeholder')}
          className="min-h-[200px] resize-none"
          maxLength={2000}
          disabled={isAlbumGenerating}
        />
      </div>
      {footer}
    </>
  );
}
